import React from "react";
import { FaTools, FaServer, FaCogs, FaPlug, FaWifi, FaSyncAlt } from "react-icons/fa";
import { motion } from "framer-motion";

const MaintenancePage = () => {
  const floatingIcons = [
    {
      icon: <FaTools className="text-blue-400" />,
      delay: 0,
      size: "text-2xl sm:text-3xl md:text-4xl",
    },
    {
      icon: <FaServer className="text-purple-400" />,
      delay: 0.3,
      size: "text-2xl sm:text-3xl md:text-4xl",
    },
    {
      icon: <FaCogs className="text-green-400" />,
      delay: 0.6,
      size: "text-2xl sm:text-3xl md:text-4xl",
    },
    {
      icon: <FaPlug className="text-yellow-400" />,
      delay: 0.9,
      size: "text-2xl sm:text-3xl md:text-4xl",
    },
    {
      icon: <FaWifi className="text-red-400" />,
      delay: 1.2,
      size: "text-2xl sm:text-3xl md:text-4xl",
    },
  ];

  const statusCards = [
    {
      icon: <FaServer className="text-blue-400" />,
      title: "Server Optimization",
      bg: "bg-gray-700",
    },
    { icon: <FaCogs className="text-purple-400" />, title: "System Updates", bg: "bg-gray-700" },
    {
      icon: <FaSyncAlt className="text-green-400" />,
      title: "Network Enhancements",
      bg: "bg-gray-700",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex flex-col items-center justify-center p-4 sm:p-6 text-center overflow-hidden relative">
      {/* Floating animated icons in background - Responsive positioning */}
      {floatingIcons.map((item, index) => (
        <motion.div
          key={index}
          initial={{ y: 0, opacity: 0 }}
          animate={{
            y: [0, -30, 0, 30, 0],
            opacity: [0, 1, 1, 1, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 15 + index * 3,
            repeat: Infinity,
            ease: "linear",
            delay: item.delay,
          }}
          className={`absolute ${item.size} opacity-20`}
          style={{
            left: `${5 + index * 20}%`,
            top: `${15 + index * 15}%`,
          }}
        >
          {item.icon}
        </motion.div>
      ))}

      {/* Main content container - Responsive sizing */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-xs sm:max-w-sm md:max-w-2xl bg-gray-800 bg-opacity-60 backdrop-blur-md p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl border border-gray-700 mx-2"
      >
        {/* Background circles - Responsive positioning */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-12 -right-12 sm:-top-16 sm:-right-16 w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full border-4 border-blue-500 border-opacity-30"
        />

        <motion.div
          animate={{ rotate: -360 }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-12 -left-12 sm:-bottom-16 sm:-left-16 w-32 h-32 sm:w-40 sm:h-40 md:w-60 md:h-60 rounded-full border-4 border-purple-500 border-opacity-20"
        />

        <div className="relative z-20 flex flex-col items-center">
          {/* Main icon - Responsive sizing */}
          <motion.div
            animate={{ y: [-3, 3, -3] }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="flex justify-center text-4xl sm:text-5xl md:text-6xl text-blue-400 mb-4 sm:mb-6"
          >
            <FaTools />
          </motion.div>

          {/* Heading - Responsive text sizing */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              System Upgrade in Progress
            </span>
          </h1>

          {/* Description - Responsive text sizing */}
          <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 max-w-md mx-auto text-center">
            We're performing critical upgrades to enhance your experience. The system will be back
            online shortly.
          </p>

          {/* Status cards - Responsive grid with centered items */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {statusCards.map((card, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.03 }}
                className={`${card.bg} bg-opacity-50 p-3 sm:p-4 rounded-lg border border-gray-600 flex flex-col items-center`}
              >
                <div className="text-blue-400  text-xl sm:text-2xl mb-2">{card.icon}</div>
                <p className="text-xs sm:text-sm font-medium text-white text-center">
                  {card.title}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Refresh button - Responsive sizing */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="inline-block bg-gradient-to-r from-blue-500 to-purple-600 p-0.5 rounded-md sm:rounded-lg"
          >
            <div className="bg-gray-900 rounded-md sm:rounded-lg px-4 py-2 sm:px-6 sm:py-3">
              <p className="text-xs sm:text-sm font-medium text-white">
                Refresh the page to check status
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Footer - Responsive text sizing */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-6 sm:mt-8 text-gray-400 text-xs sm:text-sm text-center"
      >
        <p>
          For urgent inquiries, contact{" "}
          <span className="text-blue-400">printkaro369@gmail.com</span>
        </p>
      </motion.div>
    </div>
  );
};

export default MaintenancePage;
